import { ReactElement, ReactNode, useCallback, useContext } from 'react';

import { icon_arrow_left, icon_arrow_right } from '@gn-audio/icons';
import { GtmEventType } from '@gn-audio/jss-core';

import {
  isAuthorized,
  stripHtmlTags,
  TrackingContext,
  useFallbackTranslation,
  useSiteSettings,
} from '../../../utils';
import { Button } from '../../Button/Button';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { logIn } from '../../UipMenu/utilities/logIn';
import { useBasket } from '../BasketContext';
import { createEcommercePayload } from '../utilities/basketHelpers';

import styles from '../Basket.module.css';

type MyAccountProps = {
  token: string;
};

const MyAccount = (props: MyAccountProps): ReactElement | null => {
  const { token } = props;
  const { t } = useFallbackTranslation();
  const { closeMyAccountView, basket, groups } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);
  const { sfcc } = useSiteSettings();

  const eCommercePayload = useCallback(
    (loginType: 'Registered Used' | 'Guest') =>
      createEcommercePayload({
        eventType: GtmEventType.LoginCheckout,
        basket,
        groups,
        currency: sfcc?.currency,
        loginType,
      }),
    [basket, groups, sfcc?.currency]
  );

  const logInHandler = () => {
    if (isAuthorized()) {
      return;
    }
    const afterGtm = async () => {
      await logIn([
        {
          type: 'transferBasket',
          params: { basketId: basket?.basket_id, customerId: basket?.customer_info?.customer_id },
        },
        { type: 'checkout' },
      ]);
    };
    gtmEvent(eCommercePayload('Registered Used'), afterGtm);
  };

  const continueAsGuestHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const form = e.currentTarget.form;
    const afterGtm = () => {
      form?.submit();
    };
    gtmEvent(eCommercePayload('Guest'), afterGtm);
  };

  const formatSignUpMessage = (): ReactNode => {
    const signUp = t('uip.Signup');
    const signUpNowMessage = stripHtmlTags(t('cart.SignUpNowMessage'));
    const splitSignUpMessage = signUpNowMessage.split(signUp);
    return (
      <>
        {splitSignUpMessage[0]}
        <button onClick={logInHandler} className={styles.signUpNow}>
          {signUp}
        </button>
        {splitSignUpMessage[1]}
      </>
    );
  };

  return (
    <>
      <span className={`${styles.myAccount} mb-6`}>{t('uip.MyJabraAccount')}</span>
      <div className="flex min-w-fit flex-col items-center">
        <form method="POST" action={sfcc?.basketCheckoutUrl}>
          <input type="hidden" name="token" value={token} />
          <input type="hidden" name="action" value="Checkout-Begin" />
          <Button
            variant="secondary"
            size="m"
            icon={<JabraIcon Icon={icon_arrow_right} />}
            iconPlacement="right"
            className="mb-6 w-full"
            type="submit"
            onClick={continueAsGuestHandler}
          >
            {t('cart.ContinueAsAGuest')}
          </Button>
        </form>

        <Button
          variant="primary"
          size="m"
          icon={<JabraIcon Icon={icon_arrow_right} />}
          iconPlacement="right"
          className={`mb-6 ${styles.loginButton} w-full`}
          onClick={logInHandler}
        >
          {t('uip.SignIn')}
        </Button>
      </div>
      <p className={styles.noAccountText}>{formatSignUpMessage()}</p>
      <Button
        variant="quaternary"
        size="s"
        icon={<JabraIcon Icon={icon_arrow_left} className={'h-[16px] w-[16px]'} />}
        iconPlacement="left"
        onClick={closeMyAccountView}
      >
        {t('cart.Back')}
      </Button>
    </>
  );
};

export default MyAccount;
